import React, { useState, useEffect } from 'react';
import { Button, Input, List, message, Popconfirm, Tooltip, Space } from 'antd';
import { useParams } from 'react-router-dom';
import { ScheduleOutlined } from '@ant-design/icons';
import NoteService from '../../services/NoteService';
import ReminderDrawer, { ReminderFormValues } from '../Reminders/ReminderDrawer';
import ReminderService from '../../services/ReminderService';
import CompanyService from '../../services/CompanyService';
import { formatRelativeTime, formatDate, openNotification, hasRole } from '../../utils';
import noteIcon from '../../assets/icons/note.svg';
import TrashIcon from '../../assets/icons/trash.svg';
import './Company.scss';

const { TextArea } = Input;

interface Note {
  id: number;
  content: string;
  createdAt: string;
  account?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  createdBy?: string;
}

const NoteSection: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const companyId = id ? Number(id) : null;
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [reminderDrawerOpen, setReminderDrawerOpen] = useState(false);
  const [reminderInitialValues, setReminderInitialValues] = useState<Partial<ReminderFormValues> | undefined>(undefined);
  const [companyName, setCompanyName] = useState<string | undefined>(undefined);
  const isAdmin = hasRole('ADMIN');
  const isCentralUser = hasRole('CENTRAL_USER');
  const canCreateReminder = isAdmin || isCentralUser;

  useEffect(() => {
    fetchNotes();
    fetchCompanyName();
  }, [id]);

  const fetchNotes = async () => {
    if (!id) return;
    try {
      const response = await NoteService.getNotes('company', id);
      setNotes(response.data);
    } catch (error) {
      message.error('Notlar yüklenirken bir hata oluştu.');
      console.error(error);
    }
  };

  const fetchCompanyName = async () => {
    if (!id) return;
    try {
      const resp = await CompanyService.getCompany(id);
      const name = resp?.data?.name || resp?.data?.title || resp?.data?.companyName || resp?.data?.id?.toString();
      setCompanyName(name);
    } catch (error) {
      console.error('Firma bilgisi alınamadı', error);
    }
  };

  const addNote = async () => {
    setIsLoading(true);
    if (!newNote.trim()) {
      message.warning('Lütfen notunuzu yazın.');
      setIsLoading(false);
      return;
    }

    const noteData = {
      content: newNote,
      entityName: 'company',
      entityId: Number(id),
    };

    try {
      await NoteService.postNote(noteData);
      openNotification('success', 'Başarılı', 'Not başarıyla eklendi.');
      setNewNote('');
      fetchNotes();
    } catch (error) {
      openNotification('error', 'Hata', 'Not eklenirken bir hata oluştu.');
      console.error(error);
    }
    setIsLoading(false);
  };

  const deleteNote = async (noteId: number) => {
    try {
      await NoteService.remove(noteId);
      openNotification('success', 'Başarılı', 'Not başarıyla silindi.');
      fetchNotes();
    } catch (error) {
      openNotification('error', 'Hata', 'Not silinirken bir hata oluştu.');
      console.error(error);
    }
  };

  const handleOpenReminderDrawer = (note: Note) => {
    if (!companyId) {
      openNotification('error', 'Hata', 'Firma bilgisi bulunamadı.');
      return;
    }

    setReminderInitialValues({
      activityType: 'CALL',
      entityTab: 'COMPANY',
      companyId,
      content: note.content,
    });
    setReminderDrawerOpen(true);
  };

  const handleReminderSubmit = async (values: ReminderFormValues) => {
    try {
      const entityId = values.companyId;
      const isMeeting = values.activityType === 'MEETING_OFFLINE' || values.activityType === 'MEETING_ONLINE';
      const channelDefault = values.activityType === 'MEETING_OFFLINE' ? 'IN_PERSON' : values.activityType === 'MEETING_ONLINE' ? 'ONLINE' : '';
      const resolvedChannel = values.channel ?? (isMeeting ? channelDefault : undefined);
      const reminderDate = values.reminderDate
        ? values.reminderDate.format('YYYY-MM-DDTHH:mm:ss')
        : undefined;
      const dueDate = values.dueDate ? values.dueDate.format('YYYY-MM-DDTHH:mm:ss') : undefined;
      const properties =
        values.properties && typeof values.properties === 'object'
          ? JSON.stringify(values.properties)
          : values.properties;
      const payload = {
        content: values.content,
        reminderDate,
        dueDate,
        estimatedDurationMinutes: values.estimatedDurationMinutes,
        activityType: values.activityType,
        channel: resolvedChannel,
        link: isMeeting ? values.link : undefined,
        state: 'PENDING' as const,
        ...(values.statusId ? { statusId: values.statusId } : {}),
        ...(properties ? { properties } : {}),
        ...(entityId ? { entityName: 'COMPANY' as const, entityId } : {}),
      };

      if (isAdmin) {
        await ReminderService.createForAccount({ ...payload, accountId: values.accountId });
      } else {
        await ReminderService.createForMe(payload);
      }

      openNotification('success', 'Başarılı', 'Görev oluşturuldu');
      setReminderDrawerOpen(false);
      setReminderInitialValues(undefined);
    } catch (e: any) {
      if (e?.errorFields) return;
      openNotification('error', 'Hata', e?.response?.data || 'Görev oluşturulamadı');
    }
  };

  return (
    <div>
      <div className="mb-20px">
        <h3 className="d-flex align-items-center">
          <Button
            icon={<img src={noteIcon} alt="pen" className="icon" />}
            shape="circle"
            className="icon-button mr-10px"
          />
          Not Ekle
        </h3>
        <TextArea
          placeholder="Notunuzu buraya yazınız."
          value={newNote}
          className="br-16px d-input-form"
          onChange={(e) => setNewNote(e.target.value)}
          rows={5}
        />
      </div>
      <div className="d-flex justify-content-end mb-20px">
        <Button onClick={() => setNewNote('')} className="d-button width-auto mr-10px">
          Temizle
        </Button>
        <Button type="primary" onClick={addNote} loading={isLoading} className="d-button dark-button width-auto">
          Kaydet
        </Button>
      </div>
      <h3>Notlar</h3>
      <List
        itemLayout="horizontal"
        dataSource={notes}
        locale={{ emptyText: 'Henüz not bulunmuyor.' }}
        renderItem={(note) => (
          <List.Item
            className="d-flex align-items-start"
            actions={[
              <Space key="note-actions" size={8} align="center">
                {canCreateReminder && (
                  <Tooltip title="Görev Ekle">
                    <Button
                      icon={<ScheduleOutlined />}
                      shape="circle"
                      className="icon-button ml-12px"
                      onClick={() => handleOpenReminderDrawer(note)}
                    />
                  </Tooltip>
                )}
                <Popconfirm
                  placement="left"
                  title="Bu notu silmek istediğinize emin misiniz?"
                  onConfirm={() => deleteNote(note.id)}
                  okText="Evet"
                  cancelText="Hayır"
                >
                  <Button
                    icon={<img src={TrashIcon} alt="pen" className="icon" />}
                    shape="circle"
                    className="icon-button"
                  />
                </Popconfirm>
              </Space>,
            ]}
          >
            <List.Item.Meta
              avatar={
                <Button
                  icon={<img src={noteIcon} alt="pen" className="icon" />}
                  shape="circle"
                  className="icon-button"
                />
              }
              description={
                <>
                  <div style={{ color: '#1B1E27' }}>{note.content}</div>
                  {(note.account || note.createdBy) && (
                    <div style={{ color: '#555', fontSize: 12 }}>
                      {`${note.account?.firstName ?? ''} ${note.account?.lastName ?? ''}`.trim() ||
                        'Kullanıcı'}{' '}
                      - {note.account?.email ?? note.createdBy ?? 'Bilinmiyor'}
                    </div>
                  )}
                  <Tooltip title={formatDate(note.createdAt)}>
                    <span style={{ color: '#888' }}>{formatRelativeTime(note.createdAt)}</span>
                  </Tooltip>
                </>
              }
            />
          </List.Item>
        )}
      />

      <ReminderDrawer
        open={reminderDrawerOpen}
        isEdit={false}
        isAdmin={isAdmin}
        companies={[]}
        proposals={[]}
        initialValues={reminderInitialValues}
        hideEntityTabs
        fixedCompanyId={companyId ?? undefined}
        fixedCompanyName={companyName}
        onClose={() => {
          setReminderDrawerOpen(false);
          setReminderInitialValues(undefined);
        }}
        onSubmit={handleReminderSubmit}
      />
    </div>
  );
};

export default NoteSection;
